import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface ImpersonatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  merchantId?: string;
  merchant?: { id: string; name: string; code: string } | null;
}

interface ImpersonationState {
  active: boolean;
  accessToken: string | null;
  user: ImpersonatedUser | null;
  superAdminEmail: string | null;
  expiresAt: string | null;
  start: (accessToken: string, superAdminEmail?: string) => void;
  setUser: (user: ImpersonatedUser) => void;
  stop: () => void;
}

function decodeExpiry(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? new Date(payload.exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
}

// Persisted to sessionStorage — deliberately NOT localStorage, which the
// regular auth store uses. sessionStorage is per-tab, so an impersonation
// session opened in a new tab can never bleed into (or be clobbered by) the
// Super Admin's own logged-in tab, and it disappears the moment the tab closes.
export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set) => ({
      active: false,
      accessToken: null,
      user: null,
      superAdminEmail: null,
      expiresAt: null,
      start: (accessToken, superAdminEmail) =>
        set({ active: true, accessToken, superAdminEmail: superAdminEmail ?? null, user: null, expiresAt: decodeExpiry(accessToken) }),
      setUser: (user) => set({ user }),
      stop: () => set({ active: false, accessToken: null, user: null, superAdminEmail: null, expiresAt: null }),
    }),
    {
      name: 'impersonation-storage',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
